import type { SyncrelState } from "../domain/types";

const now = 1719300000000;

export const createSeedState = (): SyncrelState => ({
  version: 1,
  workspace: {
    id: "workspace-syncrel-start",
    name: "Syncrel Product Strategy",
    room: "syncrel-start",
    activeFramework: "rice",
    enabledFrameworks: ["rice", "story-map", "revenue"],
    frameworkWelcomeSeen: ["rice", "story-map", "revenue"],
    selectedEntityId: "feature-usage-billing",
    selectedRelationId: null,
    updatedAt: now,
  },
  schema: {
    fields: [
      { key: "reach", label: "Reach", type: "number" },
      { key: "impact", label: "Impact", type: "number" },
      { key: "confidence", label: "Confidence", type: "percent" },
      { key: "effort", label: "Effort", type: "number" },
      { key: "riceScore", label: "RICE", type: "computed", readonly: true },
      { key: "contractValue", label: "Contract Value", type: "currency" },
      { key: "priorityVotes", label: "Priority Votes", type: "number" },
    ],
    relationTypes: [
      { key: "drives", label: "drives", inverseLabel: "driven by" },
      { key: "blocked-by", label: "blocked by", inverseLabel: "blocks" },
      { key: "signal-origin", label: "signal origin", inverseLabel: "originates signal" },
    ],
  },
  entities: [
    {
      id: "goal-enterprise-growth",
      type: "goal",
      name: "Expand enterprise revenue",
      status: "active",
      fields: { frameworkCanvas: "rice", owner: "Strategy", horizon: "H2" },
      doc: "# Expand enterprise revenue\n\n## Context\nGrow annual recurring revenue by packaging capabilities that reduce enterprise procurement friction.\n\n## Measure\nPipeline influenced, expansion conversion, implementation confidence.",
      position: { x: 110, y: 130 },
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "feature-usage-billing",
      type: "feature",
      name: "Usage-based billing",
      status: "planned",
      fields: {
        reach: 12000,
        impact: 2,
        confidence: 80,
        effort: 24,
        valueScore: 19200,
        riceScore: 800,
        contractValue: 120000,
        clientUrgency: "High",
        impactScore: 5,
        priorityVotes: 0,
        priority: "High",
        frameworkCanvas: "rice",
      },
      doc: "# Usage-based billing\n\n## Problem\nEnterprise customers need metered billing events before they can expand usage-based contracts.\n\n## Scope\n- Emit usage events from workspace activity\n- Produce auditable billing summaries\n- Export usage history for customer finance teams\n\n## Open Questions\n- Should invoice previews live in Syncrel or the billing system?\n- Which events are legally billable in regulated enterprise contracts?",
      position: { x: 430, y: 190 },
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "feature-linear-export",
      type: "feature",
      name: "Linear-ready engineering export",
      status: "active",
      fields: {
        reach: 6500,
        impact: 1.5,
        confidence: 90,
        effort: 12,
        valueScore: 8775,
        riceScore: 731.25,
        contractValue: 80000,
        clientUrgency: "Medium",
        impactScore: 3,
        priorityVotes: 0,
        priority: "High",
        frameworkCanvas: "rice",
      },
      doc: "# Linear-ready engineering export\n\n## Problem\nProduct teams need to hand off approved strategy items without rewriting context into delivery tickets.\n\n## Scope\n- Generate Linear-compatible JSON\n- Include Syncrel entity IDs in metadata\n- Preserve PRD summary, labels, and priority",
      position: { x: 720, y: 330 },
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "feature-inline-polls",
      type: "feature",
      name: "Inline spec polls",
      status: "planned",
      fields: {
        reach: 4200,
        impact: 1,
        confidence: 70,
        effort: 8,
        valueScore: 2940,
        riceScore: 367.5,
        contractValue: 45000,
        clientUrgency: "High",
        impactScore: 4,
        priorityVotes: 0,
        priority: "Medium",
        frameworkCanvas: "rice",
      },
      doc: "# Inline spec polls\n\n## Problem\nReaders should be able to vote on open questions while they are inside the spec, instead of leaving the document for a separate form.\n\n## Scope\n- Add polls directly inside the spec writing surface\n- Let each unique viewer vote once per poll\n- Show lightweight results beside the poll",
      polls: [
        {
          id: "poll-inline-spec-priority",
          question: "Which inline polling behavior should ship first?",
          options: [
            { id: "poll-inline-spec-priority-option-1", label: "One vote per viewer" },
            { id: "poll-inline-spec-priority-option-2", label: "Anonymous result sharing" },
          ],
          votes: [],
          createdAt: now,
          updatedAt: now,
        },
      ],
      position: { x: 430, y: 510 },
      createdAt: now,
      updatedAt: now,
    },
  ],
  relations: [
    {
      id: "rel-goal-enterprise-growth-feature-usage-billing-drives",
      fromId: "goal-enterprise-growth",
      toId: "feature-usage-billing",
      type: "drives",
      label: "drives",
      createdAt: now,
    },
    {
      id: "rel-feature-usage-billing-feature-linear-export-blocked-by",
      fromId: "feature-usage-billing",
      toId: "feature-linear-export",
      type: "blocked-by",
      label: "needs handoff",
      createdAt: now,
    },
  ],
});
